import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../config';
import axios from 'axios';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonText,
  IonIcon,
  IonButton,
  IonSpinner
} from '@ionic/react';
import {
  arrowBackOutline,
  shieldCheckmarkOutline,
  informationCircleOutline,
  personOutline,
  keyOutline
} from 'ionicons/icons';
import PIIWasher from './PIIWasher';

const PIIWasherTestHarness = () => {
  const history = useHistory();
  const [auth_token, setAuthToken] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showPIIWasher, setShowPIIWasher] = useState(false);
  const [error, setError] = useState(null);

  const loadMembers = useCallback(async () => {
    if (!auth_token.trim()) return;

    setLoadingMembers(true);
    setError(null);

    try {
      const response = await axios.get(
        `${config.apiUrl}/members_names`,
        {
          headers: {
            Authorization: `Bearer ${auth_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const sortedMembers = response.data.sort((a, b) => {
        const surnameA = a.Surname || '';
        const surnameB = b.Surname || '';
        if (surnameA !== surnameB) return surnameA.localeCompare(surnameB);
        return (a.First_name || '').localeCompare(b.First_name || '');
      });

      setMembers(sortedMembers);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members. Please check your auth token.');
    } finally {
      setLoadingMembers(false);
    }
  }, [auth_token]);

  const handleMemberSelect = (memberId) => {
    setSelectedMember(memberId);
    if (memberId) {
      setShowPIIWasher(true);
    }
  };

  useEffect(() => {
    if (auth_token.trim()) {
      loadMembers();
    } else {
      setMembers([]);
      setSelectedMember('');
      setShowPIIWasher(false);
    }
  }, [auth_token, loadMembers]);

  if (showPIIWasher && selectedMember) {
    return (
      <PIIWasher
        auth_token={auth_token}
        member_id={selectedMember}
      />
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton fill="clear" slot="start" onClick={() => history.goBack()}>
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <IonTitle>PII Washer Test Harness</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px', maxWidth: 700, margin: '0 auto' }}>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={shieldCheckmarkOutline} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                PII Washer Test Harness
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>Enter your auth token and select a member. The member's PII will be automatically loaded into the PII Washer.</p>
              </IonText>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Authentication</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonIcon icon={keyOutline} slot="start" />
                <IonLabel position="stacked">Auth Token *</IonLabel>
                <IonInput
                  value={auth_token}
                  onIonInput={(e) => setAuthToken(e.detail.value)}
                  placeholder="Enter your auth token"
                  type="text"
                  clearInput
                />
              </IonItem>
            </IonCardContent>
          </IonCard>

          {error && (
            <IonCard color="danger">
              <IonCardContent>
                <IonText color="light">
                  <IonIcon icon={informationCircleOutline} style={{ marginRight: 6 }} />
                  {error}
                </IonText>
              </IonCardContent>
            </IonCard>
          )}

          {auth_token && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Select Member</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {loadingMembers ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <IonSpinner name="crescent" />
                    <IonText>Loading members...</IonText>
                  </div>
                ) : (
                  <IonItem>
                    <IonIcon icon={personOutline} slot="start" />
                    <IonLabel position="stacked">Member *</IonLabel>
                    <IonSelect
                      value={selectedMember}
                      onIonChange={(e) => handleMemberSelect(e.detail.value)}
                      placeholder="Select a member"
                      interface="popover"
                    >
                      {members.map((member) => (
                        <IonSelectOption key={member.id} value={member.id.toString()}>
                          {member.Surname}, {member.First_name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                )}
              </IonCardContent>
            </IonCard>
          )}

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <IonButton
              expand="block"
              onClick={() => history.push('/pii-washer')}
            >
              <IonIcon icon={shieldCheckmarkOutline} slot="start" />
              Launch PII Washer (Manual Mode)
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PIIWasherTestHarness;
